export interface ICurrentBillingPlanView {
  billingPlan: IBillingPlanData;
  expires: number;
  recommendedUpgradePlan: IBillingPlanData;
}

export interface IBillingPlanData {
  id: number;
  name: string;
  billingPlanType: BillingPlanType;
  desc: string;
  enabled: boolean;
  features: string[];
  publicLabel: string;
  pricePerMonth: number;
  intervalMonths: number;
  numOfDays: number | null;
  recommendedUpgradePlanId: number | null;
  allowAds: boolean;
  limitStorageGB: number;
  limitTransferGB: number;
  limitStreams: number | null;
  limitVideos: number | null;
}

export const BILLING_PLAN_TYPES = ['normal', 'trial', 'oneoff'] as const;
export type BillingPlanType = typeof BILLING_PLAN_TYPES[number];

export interface IBillingAccountViewData {
  billingAccountId: string;
  billingAccountType: BillingAccountType;
  fullName: string;
  orgName: string | null;
  address1: string;
  city: string;
  region: string;
  postalCode: string;
  countryIso2: string;
  cardDetails: CardViewData | null;
  created: number;
  updated: number;
}

export const BILLING_ACCOUNT_TYPES = ['card', 'check', 'bank_transfer'] as const;
export type BillingAccountType = typeof BILLING_ACCOUNT_TYPES[number];

export interface CardViewData {
  cardLastFour: string;
  cardMonth: number;
  cardNum: string;
  cardType: CardType;
  cardYear: number;
  cvv: string | null;
}

export const CARD_TYPES = ['amex', 'disc', 'mastercard', 'visa'] as const;
export type CardType = typeof CARD_TYPES[number];

export interface IBillingAccountUpdateJsonResponse {
  billingAccountView: IBillingAccountViewData;
  authResult: IAuthorizeNetResult | null;
}

export interface IAuthorizeNetResult {
  data: OrderDetails;
  responseCodeName: AuthorizeResponseType;
  responseText: string;
  responseReasonCode: number;
  reason: string;
  notes: string;
}

export interface OrderDetails {
  address: string | null;
  amount: number;
  city: string | null;
  countryIso2: string | null;
  cardNum: string;
  cardMonth: string;
  cardYear: string;
  cardCVV: string | null;
  desc: string | null;
  firstName: string;
  invoiceNumber: string | null;
  lastName: string;
  poNumber: string | null;
  postalCode: string | null;
  region: string | null;
}

export const AUTHORIZE_RESPONSE_TYPES = ['approved', 'decline', 'review', 'error', 'unknown'] as const;
export type AuthorizeResponseType = typeof AUTHORIZE_RESPONSE_TYPES[number];

export interface IInvoiceViewData {
  billInfo: IInvoiceBillInfoViewData;
  billingAccountId: string;
  clientId: string;
  invoiceId: string;
  invoiceStatusId: string | null;
  invoiceStatusLabel: string | null;
  invoiceNumber: number;
  invoiceComments: string;
  invoicePurchaseOrder: string | null;
  invoiceDateCreated: number;
  invoiceDateDue: number;
  invoiceDatePaid: number | null;
  invoiceLineItems: IInvoiceLineItemViewData[];
  viewSignature: string;
  total: number;
}

export interface IInvoiceBillInfoViewData {
  fromAddr1: string;
  fromAddr2: string;
  fromAddr3: string;
  toAddr1: string;
  toAddr2: string;
  toAddr3: string;
  toAddr4: string;
}

export interface IInvoiceLineItemViewData {
  lineItemId: string;
  invoiceId: string;
  clientPlanId: string | null;
  lineItemDesc: string;
  lineItemQty: number;
  lineItemPriceAmount: number;
  lineItemDiscountPercent: number;
  lineItemTaxPercent: number;
  lineItemTotal: number;
  lineItemUpdated: number;
}

export interface IBillTotalIntervalData {
  total: number;
  start: number;
  end: number;
  renewalIntervalMonths: number;
  renewalTotal: number;
  renewalStart: number;
}

export interface IUpgradeTrialReq {
  cId: number;
  billing: IBillingAccountViewData;
  planId: number;
}

// ── Embedded Stripe card entry (billing migration; task billing-card-entry) ──────
// Contract of record: backend jvm notes/billing/cutover/ref_card_entry_contract.md
// (v1.1, 2026-09-15). Shapes below mirror it field for field; dashboard-side notes in
// notes/billing-card-entry.md. WIRE RULE: the platform JSON printer drops null fields,
// so every nullable field below arrives with its key ABSENT — typed optional here and
// coalesced at the boundary (CardEntryUtils), never read raw in components.

/** POST /v1/billing/card/setup — a fresh card-only SetupIntent on the client's Stripe Customer. */
export interface ICardSetupRes {
  clientSecret: string;
  /** From api config (pk_test on dev, pk_live in prod) — never a build-time variable. */
  publishableKey: string;
  setupIntentId: string;
}

/** Billing name/address collected by the dashboard form (never card data). */
export interface ICardBillingDetails {
  fullName: string;
  orgName: string | null;
  address1: string;
  city: string;
  region: string;
  postalCode: string;
  countryIso2: string;
}

/** POST /v1/billing/card/confirm — after Stripe.js confirmed the SetupIntent client-side. */
export interface ICardConfirmReq {
  setupIntentId: string;
  /** Optional on the wire; the dashboard always sends it so the billing account row stays current. */
  billing?: ICardBillingDetails;
}

/** Card summary (contract §1). `brand` is always a Stripe brand id. */
export interface ICardSummary {
  brand: string;
  last4: string;
  expMonth: number;
  expYear: number;
  /** ISO-8601 UTC (not epoch seconds); absent/null for rows migrated from the legacy vault. */
  updatedAt?: string | null;
}

export interface ICardConfirmRes {
  card: ICardSummary;
}

/**
 * GET /v1/billing/card — nothing on file (e.g. a trial) is `{}` with the `card` key
 * ABSENT (contract §2.3, v1.1); read through CardEntryUtils.cardFromRes.
 */
export interface ICardOnFileRes {
  card?: ICardSummary | null;
}

/** POST /v1/order/upgradetrial with the card already saved through setup/confirm (contract §2.4). */
export interface IUpgradeTrialSavedCardReq {
  cId: number;
  planId: number;
  billing?: ICardBillingDetails;
}

export const ACTIVATION_STATES = ['active', 'pending', 'payment_failed'] as const;
export type ActivationState = typeof ACTIVATION_STATES[number];

export interface IUpgradeTrialSavedCardRes {
  activation: ActivationState;
  /** Absent on `pending` (nothing charged). */
  invoiceNumber?: number | null;
  /** Customer-safe decline text on `payment_failed`; absent otherwise. */
  message?: string | null;
}

/** Machine codes in the VError envelope's `data.code` (contract §1). */
export const CARD_ENTRY_ERROR_CODES = {
  DISABLED: 'card_entry_disabled',
  NO_BILLING_CLIENT: 'no_billing_client',
  SETUP_INTENT_NOT_SUCCEEDED: 'setup_intent_not_succeeded',
  SETUP_INTENT_MISMATCH: 'setup_intent_mismatch',
  CARD_REQUIRED: 'card_required',
  STRIPE_UNAVAILABLE: 'stripe_unavailable',
} as const;
